/**
 * Bounded retry for transient storage-write failures.
 *
 * Wraps a single storage-adapter mutation (saveConversation /
 * updateConversation / etc.) in a small exponential-backoff loop.
 *
 * Retries on network errors and 5xx responses. Skips retry on 4xx —
 * those are caller bugs (bad payload, missing auth, not-found) that
 * won't recover on a second attempt and just waste a request.
 *
 * HttpStorageAdapter throws `Error("API request failed: <status> <text>")`
 * on non-2xx responses; we parse the status from the message to gate the
 * retry decision.
 */

const DEFAULT_BACKOFFS_MS: readonly number[] = [1000, 3000];

export async function retryStorageWrite(
  fn: () => Promise<void>,
  backoffsMs: readonly number[] = DEFAULT_BACKOFFS_MS
): Promise<void> {
  let attempt = 0;
  for (;;) {
    try {
      await fn();
      return;
    } catch (error) {
      const isLastAttempt = attempt >= backoffsMs.length;
      if (isLastAttempt || !isRetryable(error)) {
        throw error;
      }
      const delay = backoffsMs[attempt];
      console.warn(
        `[retryStorageWrite] attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
        error instanceof Error ? error.message : error
      );
      await sleep(delay);
      attempt++;
    }
  }
}

function isRetryable(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  const match = msg.match(/API request failed:\s*(\d{3})/);
  if (match) {
    const status = parseInt(match[1], 10);
    if (status >= 400 && status < 500) return false;
  }
  return true;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
